/**
 * useReports Hook
 *
 * Fetches and manages reports for the current user's client.
 * Auto-fetches on mount when authenticated.
 * Supports impersonation mode - loads reports as if user is the impersonated user.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import { useToast } from '../contexts/ToastContext';

export const useReports = () => {
  const {
    isAuthenticated,
    isImpersonating,
    impersonatedUser,
    impersonatedClient,
  } = useAuth();
  const { state, setReports } = useApp();
  const { showError } = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track the last impersonation state to detect changes
  const lastImpersonationRef = useRef<string | null>(null);

  /**
   * Fetch reports from API
   * - Normal mode: fetches reports based on user's permissions
   * - Impersonation mode: fetches reports as the impersonated user would see them
   */
  const fetchReports = useCallback(async () => {
    if (!isAuthenticated) return;

    setIsLoading(true);
    setError(null);

    try {
      let response;

      if (isImpersonating && impersonatedUser) {
        // Impersonation mode - load reports as the impersonated user would see them
        if (impersonatedUser.role === 'viewer') {
          // Viewer: Get their specific permissions and load those reports
          const permissionsRes = await api.admin.getViewerPermissions(impersonatedUser.id);
          if (permissionsRes.error) {
            throw new Error(permissionsRes.error);
          }

          const permittedReportIds = permissionsRes.data?.permissions || [];

          if (permittedReportIds.length === 0) {
            setReports([]);
            return;
          }

          // Get all reports and filter to only permitted ones
          const reportsRes = await api.admin.getReports();
          if (reportsRes.error) {
            throw new Error(reportsRes.error);
          }

          const allReports = reportsRes.data || [];
          const viewerReports = allReports.filter((r) =>
            permittedReportIds.includes(r.id)
          );
          const sortedReports = viewerReports.sort((a, b) => a.order - b.order);
          setReports(sortedReports);
          return;
        } else if (impersonatedUser.role === 'admin' && impersonatedClient) {
          // Admin: Get all reports for their client
          const reportsRes = await api.admin.getReports();
          if (reportsRes.error) {
            throw new Error(reportsRes.error);
          }

          const allReports = reportsRes.data || [];
          const clientReports = allReports.filter(
            (r) => r.clientId === impersonatedClient.id
          );
          const sortedReports = clientReports.sort((a, b) => a.order - b.order);
          setReports(sortedReports);
          return;
        }
      }

      // Normal mode - fetch reports as usual
      response = await api.reports.getAll();

      if (response.error) {
        setError(response.error);
        showError(`Failed to load reports: ${response.error}`);
        return;
      }

      if (response.data) {
        // Sort reports by order
        const sortedReports = [...response.data].sort((a, b) => a.order - b.order);
        setReports(sortedReports);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load reports';
      setError(message);
      showError(message);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, isImpersonating, impersonatedUser, impersonatedClient, setReports, showError]);

  /**
   * Auto-fetch reports on mount when authenticated
   * Clear reports when user logs out
   */
  useEffect(() => {
    if (isAuthenticated && state.reports.length === 0) {
      fetchReports();
    } else if (!isAuthenticated && state.reports.length > 0) {
      // Clear reports when user logs out
      setReports([]);
    }
  }, [isAuthenticated, state.reports.length, fetchReports, setReports]);

  /**
   * Re-fetch reports when impersonation state changes
   */
  useEffect(() => {
    const currentImpersonation = isImpersonating ? impersonatedUser?.id ?? 'impersonating' : null;

    if (lastImpersonationRef.current !== currentImpersonation) {
      // Clear existing reports immediately to prevent stale data
      setReports([]);
      lastImpersonationRef.current = currentImpersonation;
      // Refetch if we're authenticated (not on initial mount)
      if (isAuthenticated) {
        fetchReports();
      }
    }
  }, [isAuthenticated, isImpersonating, impersonatedUser?.id, fetchReports, setReports]);

  return {
    reports: state.reports,
    currentReport: state.currentReport,
    isLoading,
    error,
    reload: fetchReports,
  };
};

export default useReports;
